import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import MainCard from 'components/MainCard';
import useCreate from 'hooks/useCreate';
import useDelete from 'hooks/useDelete';
import useGet from 'hooks/useGet';
import useUpdate from 'hooks/useUpdate';
import { useSWRConfig } from 'swr';
import { triggerPriceUpdateOne } from 'utils/priceUpdateAll';

const createEmptyForm = () => ({
  id: '',
  sku: '',
  title: '',
  description: '',
  salePriceINR: '',
  salePriceUSD: '',
  stock: '0',
  isPublished: false,
  isFeatured: false,
  heroImageUrl: '',
  galleryImageUrls: []
});

const getCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getProductImage = (product) =>
  product?.heroImage ||
  product?.media?.heroImage ||
  product?.media?.galleryImages?.[0] ||
  (Array.isArray(product?.galleryImages) ? product.galleryImages[0] : '') ||
  '';

const formatMoney = (currency, amount) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return '-';

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2
    }).format(numericAmount);
  } catch {
    return `${currency} ${numericAmount.toFixed(2)}`;
  }
};

export default function CustomProductPage() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(createEmptyForm());
  const [heroImage, setHeroImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [heroPreview, setHeroPreview] = useState('');
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [search, setSearch] = useState('');

  const { mutate } = useSWRConfig();
  const { data, loading, error } = useGet('/api/products/');
  const { create: createProduct } = useCreate('/api/products');
  const { update: updateProduct } = useUpdate('/api/products');
  const { remove: deleteProduct } = useDelete('/api/products');

  useEffect(() => () => {
    if (heroPreview) URL.revokeObjectURL(heroPreview);
    galleryPreviews.forEach((preview) => URL.revokeObjectURL(preview));
  }, [galleryPreviews, heroPreview]);

  const products = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return getCollection(data)
      .filter((product) => Boolean(product?.isCustomize))
      .filter((product) => {
        if (!normalizedSearch) return true;

        return [product?.title, product?.SKU, product?.sku, product?.description]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch));
      });
  }, [data, search]);

  const resetForm = () => {
    if (heroPreview) URL.revokeObjectURL(heroPreview);
    galleryPreviews.forEach((preview) => URL.revokeObjectURL(preview));

    setForm(createEmptyForm());
    setHeroImage(null);
    setGalleryImages([]);
    setHeroPreview('');
    setGalleryPreviews([]);
    setSaveError('');
  };

  const openDrawer = () => {
    resetForm();
    setSuccessMessage('');
    setDrawerOpen(true);
  };

  const openEditDrawer = (product) => {
    resetForm();
    setSuccessMessage('');
    setForm({
      id: product?._id || product?.id || '',
      sku: product?.SKU || product?.sku || '',
      title: product?.title || '',
      description: product?.description || '',
      salePriceINR:
        product?.salePriceINR === null || typeof product?.salePriceINR === 'undefined'
          ? ''
          : String(product.salePriceINR),
      salePriceUSD:
        product?.salePriceUSD === null || typeof product?.salePriceUSD === 'undefined'
          ? ''
          : String(product.salePriceUSD),
      stock:
        product?.stock === null || typeof product?.stock === 'undefined'
          ? '0'
          : String(product.stock),
      isPublished:
        typeof product?.isPublished === 'boolean'
          ? product.isPublished
          : Boolean(product?.publishStatus),
      isFeatured: Boolean(product?.isFeatured),
      heroImageUrl: product?.heroImage || product?.media?.heroImage || '',
      galleryImageUrls: Array.isArray(product?.media?.galleryImages)
        ? product.media.galleryImages.filter(Boolean)
        : Array.isArray(product?.galleryImages)
          ? product.galleryImages.filter(Boolean)
          : []
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    resetForm();
  };

  const handleInputChange = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value
    }));
  };

  const handleHeroImageChange = (file) => {
    if (heroPreview) URL.revokeObjectURL(heroPreview);
    setHeroImage(file);
    setHeroPreview(file ? URL.createObjectURL(file) : '');
  };

  const handleGalleryImageChange = (files) => {
    galleryPreviews.forEach((preview) => URL.revokeObjectURL(preview));
    setGalleryImages(files);
    setGalleryPreviews(files.map((file) => URL.createObjectURL(file)));
  };

  const buildPayload = () => {
    const payload = new FormData();

    payload.append('SKU', form.sku.trim().toUpperCase());
    payload.append('title', form.title.trim());
    payload.append('description', form.description.trim());
    payload.append('isCustomize', 'true');
    payload.append('stock', String(Number(form.stock || 0)));
    payload.append('isFeatured', String(Boolean(form.isFeatured)));
    payload.append('isPublished', String(Boolean(form.isPublished)));
    payload.append('publishStatus', String(Boolean(form.isPublished)));

    if (form.salePriceINR !== '' && Number.isFinite(Number(form.salePriceINR))) {
      payload.append('salePriceINR', String(Number(form.salePriceINR)));
    }

    if (form.salePriceUSD !== '' && Number.isFinite(Number(form.salePriceUSD))) {
      payload.append('salePriceUSD', String(Number(form.salePriceUSD)));
    }

    if (form.id) {
      payload.append('media[heroImage]', form.heroImageUrl || '');
      form.galleryImageUrls.forEach((url) => {
        payload.append('media[galleryImages][]', url);
      });
    }

    if (heroImage) {
      payload.append('heroImage', heroImage);
    }

    galleryImages.forEach((file) => {
      payload.append('galleryImages', file);
    });

    return payload;
  };

  const handleSubmit = async () => {
    setSaveError('');
    setSuccessMessage('');

    if (!form.sku.trim()) {
      setSaveError('SKU is required.');
      return;
    }

    if (!form.title.trim()) {
      setSaveError('Title is required.');
      return;
    }

    const hasINRPrice = form.salePriceINR !== '' && Number.isFinite(Number(form.salePriceINR));
    const hasUSDPrice = form.salePriceUSD !== '' && Number.isFinite(Number(form.salePriceUSD));

    if (!hasINRPrice && !hasUSDPrice) {
      setSaveError('Add at least one price.');
      return;
    }

    setSaving(true);

    try {
      const payload = buildPayload();

      let savedProduct;
      if (form.id) {
        savedProduct = await updateProduct(form.id, payload, { revalidate: false });
      } else {
        savedProduct = await createProduct(payload, { revalidate: false });
      }

      const productId =
        form.id ||
        savedProduct?._id ||
        savedProduct?.product?._id ||
        savedProduct?.data?._id;

      try {
        await triggerPriceUpdateOne(productId);
      } catch (priceError) {
        console.warn('Price update-one failed after custom product save:', priceError);
      }

      await mutate('/api/products/');
      setSuccessMessage(
        form.id ? 'Custom product updated successfully.' : 'Custom product created successfully.'
      );
      setDrawerOpen(false);
      resetForm();
    } catch (requestError) {
      setSaveError(
        requestError?.response?.data?.message ||
          requestError?.response?.data?.error ||
          `Failed to ${form.id ? 'update' : 'create'} custom product.`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    const productId = product?._id || product?.id;
    if (!productId) return;

    const confirmed = window.confirm(`Delete "${product?.title || product?.SKU || 'this product'}"?`);
    if (!confirmed) return;

    setSaveError('');
    setSuccessMessage('');
    try {
      await deleteProduct(productId, { revalidate: false });
      await mutate('/api/products/');
      setSuccessMessage('Custom product deleted successfully.');
    } catch (requestError) {
      setSaveError(
        requestError?.response?.data?.message ||
          requestError?.response?.data?.error ||
          'Failed to delete custom product.'
      );
    }
  };

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Box>
          <Typography variant="h4">Custom Product</Typography>
          <Typography color="text.secondary">
            Create custom products with the standard product API and `isCustomize` enabled.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openDrawer}>
          Add Custom Product
        </Button>
      </Stack>

      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
      {saveError && !drawerOpen ? <Alert severity="error">{saveError}</Alert> : null}
      {error ? <Alert severity="error">Failed to load custom products.</Alert> : null}

      <MainCard>
        <TextField
          fullWidth
          size="small"
          label="Search custom products"
          placeholder="Search by SKU, title or description"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </MainCard>

      <MainCard title={`Custom Products (${products.length})`}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Stock</TableCell>
                <TableCell>Sale Price INR</TableCell>
                <TableCell>Sale Price USD</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>Featured</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading custom products...
                  </TableCell>
                </TableRow>
              ) : products.length ? (
                products.map((product, index) => {
                  const image = getProductImage(product);
                  const productId = product?._id || product?.id || `${product?.SKU || 'custom'}-${index}`;

                  return (
                    <TableRow key={productId} hover>
                      <TableCell sx={{ maxWidth: 380, minWidth: 220 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={image || undefined}
                            variant="rounded"
                            sx={{ width: 48, height: 48, flexShrink: 0 }}
                          >
                            {product?.title?.charAt(0) || 'C'}
                          </Avatar>
                          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block'
                              }}
                              title={product?.title}
                            >
                              {product?.title || 'Untitled Product'}
                            </Typography>
                            {product?.description ? (
                              <Tooltip title={product.description} placement="bottom-start" arrow>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    mt: 0.25
                                  }}
                                >
                                  {product.description}
                                </Typography>
                              </Tooltip>
                            ) : null}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{product?.SKU || product?.sku || '-'}</TableCell>
                      <TableCell>{Number(product?.stock || 0)}</TableCell>
                      <TableCell>{formatMoney('INR', product?.salePriceINR)}</TableCell>
                      <TableCell>{formatMoney('USD', product?.salePriceUSD)}</TableCell>
                      <TableCell>{product?.isPublished || product?.publishStatus ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{product?.isFeatured ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openEditDrawer(product)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(product)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No custom products found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </MainCard>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 520 },
            maxWidth: '100%'
          }
        }}
      >
        <Stack spacing={2.5} sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5">
                {form.id ? 'Edit Custom Product' : 'Add Custom Product'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This form always sends `isCustomize` as true.
              </Typography>
            </Box>
            <IconButton onClick={closeDrawer} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {saveError ? <Alert severity="error">{saveError}</Alert> : null}

          <TextField
            label="SKU"
            value={form.sku}
            onChange={(event) => handleInputChange('sku', event.target.value)}
            required
          />

          <TextField
            label="Title"
            value={form.title}
            onChange={(event) => handleInputChange('title', event.target.value)}
            required
          />

          <TextField
            label="Description"
            value={form.description}
            onChange={(event) => handleInputChange('description', event.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
              }
            }}
            multiline
            minRows={3}
            sx={{ '& textarea': { resize: 'vertical' } }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Stock"
              type="number"
              value={form.stock}
              onChange={(event) => handleInputChange('stock', event.target.value)}
            />
            <TextField
              fullWidth
              label="Sale Price INR"
              type="number"
              value={form.salePriceINR}
              onChange={(event) => handleInputChange('salePriceINR', event.target.value)}
            />
            <TextField
              fullWidth
              label="Sale Price USD"
              type="number"
              value={form.salePriceUSD}
              onChange={(event) => handleInputChange('salePriceUSD', event.target.value)}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isPublished}
                  onChange={(event) => handleInputChange('isPublished', event.target.checked)}
                />
              }
              label="Published"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isFeatured}
                  onChange={(event) => handleInputChange('isFeatured', event.target.checked)}
                />
              }
              label="Featured"
            />
          </Stack>

          <Button component="label" variant="outlined">
            Upload Hero Image
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={(event) => handleHeroImageChange(event.target.files?.[0] || null)}
            />
          </Button>

          {heroPreview || form.heroImageUrl ? (
            <Box
              component="img"
              src={heroPreview || form.heroImageUrl}
              alt="Hero preview"
              sx={{
                width: 140,
                height: 140,
                objectFit: 'cover',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            />
          ) : null}

          <Button component="label" variant="outlined">
            Upload Gallery Images
            <input
              hidden
              type="file"
              accept="image/*"
              multiple
              onChange={(event) =>
                handleGalleryImageChange(Array.from(event.target.files || []))
              }
            />
          </Button>

          {form.galleryImageUrls.length || galleryPreviews.length ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {[...form.galleryImageUrls, ...galleryPreviews].map((preview, index) => (
                <Box
                  key={`${preview}-${index}`}
                  component="img"
                  src={preview}
                  alt={`Gallery preview ${index + 1}`}
                  sx={{
                    width: 88,
                    height: 88,
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                />
              ))}
            </Stack>
          ) : null}

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button onClick={closeDrawer} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : form.id ? 'Update Product' : 'Create Product'}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </Stack>
  );
}
